import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import {
  ChevronUp, ChevronDown, Plus, Trash2, Save, CheckCircle,
  CreditCard, FlaskConical, Image, Clock, X, Truck as TruckIcon,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Combobox from '../components/Combobox';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const today = () => new Date().toISOString().split('T')[0];
const newItemId = () => `si_${Date.now()}_${Math.random().toString(36).slice(2)}`;
const newPgId = () => `pg_${Date.now()}_${Math.random().toString(36).slice(2)}`;
const normalizeTruck = (v) => (v || '').toUpperCase().replace(/\s+/g, '');

const emptySubItem = () => ({
  item_id: newItemId(),
  grade_id: '', grade_name: '',
  quantity: '', price: '', bgn_number: '', production_year: '', prod_year_id: '',
  penalty: '',
});

const emptyPartyGroup = () => ({
  pg_id: newPgId(),
  bill_to_name: '', bill_to_address: '', bill_to_gst: '',
  bill_to_is_194q: false,
  ship_to_name: '', ship_to_address: '', ship_to_gst: '',
  subitems: [emptySubItem()],
  // Payment details
  show_payment: false,
  payment_date: today(),
  payment_amount: '',
  payment_details: '',
  payment_image_base64: '',
  // Accordion
  expanded: true,
});

// ─── GradeRow ─────────────────────────────────────────────────────────────────

function GradeRow({ si, availableGrades, isBroker, showDelete, onGrade, onUpdate, onRemove }) {
  // Display short_name in the dropdown; fall back to name if short_name is blank
  const displayGrades = availableGrades.map(g => ({
    ...g,
    _display: g.short_name ? g.short_name : g.name,
  }));

  return (
    <tr className="border-b border-slate-100 last:border-0">
      <td className="pr-2 py-1.5 min-w-[140px]">
        <Combobox
          items={displayGrades}
          value={si.grade_id}
          onChange={(id, g) => onGrade(id, g)}
          valueKey="grade_id"
          searchKey="_display"
          placeholder="Select grade"
          canCreate={false}
        />
      </td>
      <td className="px-2 py-1.5 w-24">
        <Input
          type="number"
          value={si.quantity}
          onChange={e => onUpdate({ quantity: e.target.value })}
          placeholder="0"
          className="text-sm text-right w-full"
          min="0"
        />
      </td>
      {!isBroker && (
        <>
          <td className="px-2 py-1.5 w-28">
            <Input
              type="number"
              value={si.price}
              onChange={e => onUpdate({ price: e.target.value })}
              placeholder="—"
              className="text-sm text-right w-full"
              min="0"
            />
          </td>
          <td className="px-2 py-1.5 w-24">
            <Input
              type="number"
              value={si.penalty}
              onChange={e => onUpdate({ penalty: e.target.value })}
              placeholder="0"
              className="text-sm text-right w-full"
              min="0"
            />
          </td>
          <td className="px-2 py-1.5 w-28">
            <Input
              value={si.bgn_number}
              onChange={e => onUpdate({ bgn_number: e.target.value })}
              placeholder="BGN #"
              className="text-sm font-mono w-full"
            />
          </td>
        </>
      )}
      <td className="pl-2 py-1.5 w-8">
        {showDelete && (
          <button onClick={onRemove} className="p-1 text-slate-300 hover:text-red-500 rounded">
            <Trash2 size={13} />
          </button>
        )}
      </td>
    </tr>
  );
}

// ─── PaymentSection ───────────────────────────────────────────────────────────
function PaymentSection({ pg, pgIdx, onUpdate }) {
  const handleImage = (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { alert('Image too large (max 3 MB)'); return; }
    const reader = new FileReader();
    reader.onloadend = () => onUpdate({ payment_image_base64: reader.result });
    reader.readAsDataURL(file);
  };

  return (
    <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
      <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Payment Details</div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Date</Label>
          <Input
            type="date"
            value={pg.payment_date}
            onChange={e => onUpdate({ payment_date: e.target.value })}
            className="text-sm w-full"
            data-testid={`payment-date-${pgIdx}`}
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Amount (₹)</Label>
          <Input
            type="number"
            value={pg.payment_amount}
            onChange={e => onUpdate({ payment_amount: e.target.value })}
            placeholder="0"
            className="text-sm w-full text-right"
            min="0"
            data-testid={`payment-amount-${pgIdx}`}
          />
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-500 mb-1 block">Details</Label>
        <textarea
          value={pg.payment_details}
          onChange={e => onUpdate({ payment_details: e.target.value })}
          placeholder="Transaction reference, notes..."
          rows={2}
          className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          data-testid={`payment-details-${pgIdx}`}
        />
      </div>
      <div>
        <Label className="text-xs text-slate-500 mb-1 block">Image</Label>
        {pg.payment_image_base64 ? (
          <div className="flex items-center gap-3">
            <img src={pg.payment_image_base64} alt="Payment" className="max-h-16 max-w-[120px] rounded border border-slate-200 object-contain" />
            <button
              onClick={() => onUpdate({ payment_image_base64: '' })}
              className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
              data-testid={`payment-remove-img-${pgIdx}`}
            >
              <X size={12} /> Remove
            </button>
          </div>
        ) : (
          <input
            type="file"
            accept="image/*"
            onChange={e => handleImage(e.target.files[0])}
            className="block text-xs text-slate-500 file:mr-3 file:py-1 file:px-3 file:rounded file:border file:border-slate-300 file:text-xs file:bg-white file:text-slate-600 hover:file:bg-slate-50 cursor-pointer"
            data-testid={`payment-image-${pgIdx}`}
          />
        )}
      </div>
    </div>
  );
}

// ─── PartyGroupCard ────────────────────────────────────────────────────────────
function PartyGroupCard({
  pg, pgIdx, totalGroups, isBroker, availableGrades, parties,
  onUpdatePg, onUpdateSi, onGrade, onAddSi, onRemoveSi,
  onRemovePg, onToggleExpand, onBillToSelect, onBillToQuickAdd,
  brokerReadOnly,
}) {
  const totalQty = pg.subitems.reduce((s, si) => s + (parseFloat(si.quantity) || 0), 0);
  const label = `P ${pgIdx + 1}`;

  if (!pg.expanded) {
    return (
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div
          className="flex items-center justify-between px-4 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
          onClick={onToggleExpand}
          data-testid={`party-header-collapsed-${pgIdx}`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded">{label}</span>
            <span className="text-sm font-medium text-slate-700 truncate">{pg.bill_to_name || '—'}</span>
            <span className="text-xs text-slate-400 shrink-0">
              {totalQty > 0 ? `${totalQty.toFixed(2)} Qtls` : '—'}
            </span>
            {pg.show_payment && pg.payment_amount && (
              <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded ml-1">
                ₹{pg.payment_amount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!brokerReadOnly && totalGroups > 1 && (
              <button
                onClick={e => { e.stopPropagation(); onRemovePg(); }}
                className="p-1 text-slate-300 hover:text-red-500 rounded"
                title="Remove this party"
                data-testid={`remove-party-${pgIdx}`}
              ><Trash2 size={13} /></button>
            )}
            <ChevronDown size={15} className="text-slate-400" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      {/* Accordion Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-blue-50 border-b border-slate-200">
        <div
          className="flex items-center gap-2 cursor-pointer flex-1"
          onClick={onToggleExpand}
          data-testid={`party-header-expanded-${pgIdx}`}
        >
          <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded">{label}</span>
          {pg.bill_to_name && <span className="text-xs text-slate-500 truncate">{pg.bill_to_name}</span>}
        </div>
        <div className="flex items-center gap-2">
          {!brokerReadOnly && totalGroups > 1 && (
            <button
              onClick={e => { e.stopPropagation(); onRemovePg(); }}
              className="p-1 text-slate-400 hover:text-red-500 rounded"
              title="Remove this party"
              data-testid={`remove-party-${pgIdx}`}
            ><Trash2 size={13} /></button>
          )}
          <button onClick={onToggleExpand} className="p-1 text-slate-400 hover:text-slate-700 rounded">
            <ChevronUp size={15} />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Bill To / Ship To */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Bill To */}
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Bill To *</div>
            <Combobox
              items={parties}
              value={pg.bill_to_name}
              onChange={(id, p) => onBillToSelect(id, p)}
              valueKey="name"
              searchKey="name"
              placeholder="Search by name or GSTIN..."
              canCreate={true}
              onQuickAdd={name => onBillToQuickAdd(name)}
              size="sm"
              disabled={brokerReadOnly}
              data-testid={`bill-to-combobox-${pgIdx}`}
            />
            {pg.bill_to_name && (
              <div className="mt-2 space-y-1.5">
                <Input
                  value={pg.bill_to_address}
                  onChange={e => onUpdatePg({ bill_to_address: e.target.value })}
                  placeholder="Address"
                  className="text-xs"
                  disabled={brokerReadOnly}
                  data-testid={`bill-to-address-${pgIdx}`}
                />
                <Input
                  value={pg.bill_to_gst}
                  onChange={e => onUpdatePg({ bill_to_gst: e.target.value })}
                  placeholder="GSTIN"
                  className="text-xs font-mono"
                  disabled={brokerReadOnly}
                  data-testid={`bill-to-gst-${pgIdx}`}
                />
              </div>
            )}
          </div>

          {/* Ship To */}
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Ship To *</div>
            {!brokerReadOnly && (
              <button
                onClick={() => onUpdatePg({
                  ship_to_name: pg.bill_to_name,
                  ship_to_address: pg.bill_to_address,
                  ship_to_gst: pg.bill_to_gst,
                })}
                className={`mb-2 flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded border transition-colors
                  ${pg.bill_to_name
                    ? 'text-blue-700 border-blue-200 bg-blue-50 hover:bg-blue-100 cursor-pointer'
                    : 'text-slate-400 border-slate-200 bg-slate-50 cursor-not-allowed'
                  }`}
                disabled={!pg.bill_to_name}
                type="button"
                data-testid={`same-as-bill-to-${pgIdx}`}
              >
                ↳ Same as Bill To
              </button>
            )}
            <Combobox
              items={parties}
              value={pg.ship_to_name}
              onChange={(id, p) => onUpdatePg({ ship_to_name: p.name, ship_to_address: p.address || '', ship_to_gst: p.gst || '' })}
              valueKey="name"
              searchKey="name"
              placeholder="Search by name or GSTIN..."
              canCreate={true}
              onQuickAdd={name => onUpdatePg({ ship_to_name: name, ship_to_address: '', ship_to_gst: '' })}
              size="sm"
              disabled={brokerReadOnly}
              data-testid={`ship-to-combobox-${pgIdx}`}
            />
            {pg.ship_to_name && (
              <div className="mt-2 space-y-1.5">
                <Input
                  value={pg.ship_to_address}
                  onChange={e => onUpdatePg({ ship_to_address: e.target.value })}
                  placeholder="Address"
                  className="text-xs"
                  disabled={brokerReadOnly}
                  data-testid={`ship-to-address-${pgIdx}`}
                />
                <Input
                  value={pg.ship_to_gst}
                  onChange={e => onUpdatePg({ ship_to_gst: e.target.value })}
                  placeholder="GSTIN"
                  className="text-xs font-mono"
                  disabled={brokerReadOnly}
                  data-testid={`ship-to-gst-${pgIdx}`}
                />
              </div>
            )}
          </div>
        </div>

        {/* Grade Items Table */}
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Items</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <colgroup>
                <col className="min-w-[140px]" />
                <col className="w-24" />
                {!isBroker && <col className="w-28" />}
                {!isBroker && <col className="w-24" />}
                {!isBroker && <col className="w-28" />}
                <col className="w-8" />
              </colgroup>
              <thead>
                <tr className="bg-slate-50 border border-slate-200">
                  <th className="text-left px-2 py-2 text-xs font-semibold text-slate-500 border-b border-slate-200">Grade</th>
                  <th className="text-right px-2 py-2 text-xs font-semibold text-slate-500 border-b border-slate-200">Qty (Qtls)</th>
                  {!isBroker && <th className="text-right px-2 py-2 text-xs font-semibold text-slate-500 border-b border-slate-200">Rate/Qtl</th>}
                  {!isBroker && <th className="text-right px-2 py-2 text-xs font-semibold text-slate-500 border-b border-slate-200">Penalty (₹)</th>}
                  {!isBroker && <th className="px-2 py-2 text-xs font-semibold text-slate-500 border-b border-slate-200">BGN #</th>}
                  <th className="w-8 border-b border-slate-200" />
                </tr>
              </thead>
              <tbody>
                {pg.subitems.map((si, siIdx) => (
                  <GradeRow
                    key={si.item_id}
                    si={si}
                    availableGrades={availableGrades}
                    isBroker={isBroker}
                    showDelete={!brokerReadOnly && pg.subitems.length > 1}
                    onGrade={(id, g) => onUpdateSi(siIdx, { grade_id: id, grade_name: g?.name || '' })}
                    onUpdate={updates => onUpdateSi(siIdx, updates)}
                    onRemove={() => onRemoveSi(siIdx)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {!brokerReadOnly && (
            <div className="flex items-center mt-2 pt-2 border-t border-slate-100">
              <button
                onClick={onAddSi}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                data-testid={`add-row-${pgIdx}`}
              >
                <Plus size={12} /> Add Row
              </button>
            </div>
          )}
        </div>

        {/* Payment Details Toggle + Section */}
        {!brokerReadOnly && (
          <div>
            <button
              onClick={() => onUpdatePg({ show_payment: !pg.show_payment })}
              className={`flex items-center gap-1.5 text-xs font-medium py-1.5 px-2.5 rounded-md border transition-colors
                ${pg.show_payment
                  ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                  : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                }`}
              data-testid={`toggle-payment-${pgIdx}`}
            >
              <CreditCard size={12} />
              {pg.show_payment ? 'Hide Payment Details' : 'Add Payment Details'}
            </button>
            {pg.show_payment && (
              <PaymentSection pg={pg} pgIdx={pgIdx} onUpdate={updates => onUpdatePg(updates)} />
            )}
          </div>
        )}
        {/* Show payment info in read-only mode */}
        {brokerReadOnly && pg.show_payment && pg.payment_amount && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="text-xs font-semibold text-green-700 mb-1">Payment Details</div>
            <div className="text-xs text-slate-600">
              Date: {pg.payment_date} &nbsp;|&nbsp; Amount: ₹{pg.payment_amount}
              {pg.payment_details && <> &nbsp;|&nbsp; {pg.payment_details}</>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── BillingForm ──────────────────────────────────────────────────────────────
export default function BillingForm() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isBroker = user?.role === 'BROKER';
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isAdminOrSuperAdmin = ['SUPER_ADMIN', 'ADMIN'].includes(user?.role);

  // ─── Master data ───────────────────────────────────────────────────────────
  const [mills, setMills] = useState([]);
  const [parties, setParties] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [grades, setGrades] = useState([]);
  const [millGradesMap, setMillGradesMap] = useState({}); // mill_id → [grades]
  const [millProdYearsMap, setMillProdYearsMap] = useState({}); // mill_id → [{prod_year_id, is_default}]
  const [prodYears, setProdYears] = useState([]); // ProductionYear[]
  const [brokers, setBrokers] = useState([]);
  const [states, setStates] = useState([]);
  // ─── Form state ────────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    sale_order_flag: true,
    is_credit: false,
    mill_id: '',
    prod_year_id: '',
    prod_year: '',
    truck_id: '',
    truck_no: '',
    driver_name: '',
    assigned_to_broker_id: '',
    include_lab_reports: false,
  });
  const [partyGroups, setPartyGroups] = useState([emptyPartyGroup()]);

  // ─── Page state ────────────────────────────────────────────────────────────
  const [existingOrder, setExistingOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [pageLoading, setPageLoading] = useState(!!orderId);

  // Broker-specific approval flows
  const [brokerReadOnly, setBrokerReadOnly] = useState(false);
  const [truckEditModal, setTruckEditModal] = useState(null); // { new_truck_no, new_truck_id, reason }
  const [hasPendingTruckEdit, setHasPendingTruckEdit] = useState(false);
  const [saleOrderEditModal, setSaleOrderEditModal] = useState(null); // { reason }
  const [hasPendingSaleOrderEdit, setHasPendingSaleOrderEdit] = useState(false);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  // ─── Derived: grades for selected top-level mill ────────────────────────────
  const gradesForMill = form.mill_id
    ? (millGradesMap[form.mill_id] || [])
    : grades;

  // Available prod years for selected mill
  const availableProdYears = useMemo(() => {
    if (!form.mill_id) return prodYears;
    const assigned = millProdYearsMap[form.mill_id] || [];
    if (assigned.length === 0) return prodYears; // no assignment → show all
    const assignedIds = new Set(assigned.map(l => l.prod_year_id));
    return prodYears.filter(py => assignedIds.has(py.prod_year_id));
  }, [form.mill_id, millProdYearsMap, prodYears]);

  // ─── Load master data ──────────────────────────────────────────────────────
  const loadMasters = useCallback(async () => {
    try {
      const reqs = [
        api.get('/masters/mills'),
        api.get('/masters/parties'),
        api.get('/masters/trucks'),
        api.get('/masters/grades'),
        api.get('/masters/prod-years'),
      ];
      if (isAdminOrSuperAdmin) reqs.push(api.get('/auth/users'));
      const results = await Promise.all(reqs);
      const loadedMills = results[0].data || [];
      const loadedGrades = results[3].data || [];
      setMills(loadedMills);
      setParties(results[1].data || []);
      setTrucks(results[2].data || []);
      setGrades(loadedGrades);
      setProdYears(results[4].data || []);
      if (isAdminOrSuperAdmin && results[5]) {
        setBrokers((results[5].data || []).filter(u => u.role === 'BROKER'));
      }

      // Prefetch mill-grade links AND assigned prod years per mill
      const mgMap = {};
      const pyMap = {};
      await Promise.all(loadedMills.map(async m => {
        try {
          const [gradeRes, pyRes] = await Promise.all([
            api.get(`/masters/mills/${m.mill_id}/grades`),
            api.get(`/masters/mills/${m.mill_id}/assigned-prod-years`),
          ]);
          mgMap[m.mill_id] = gradeRes.data || [];
          pyMap[m.mill_id] = pyRes.data || [];
        } catch {
          // Per-mill grade/prod-year fetch failed — mill will show with no grades
        }
      }));
      setMillGradesMap(mgMap);
      setMillProdYearsMap(pyMap);
    } catch (e) {
      console.error('Error loading masters:', e);
    }
  }, [isAdminOrSuperAdmin]);

  useEffect(() => { loadMasters(); }, [loadMasters]);

  // Auto-select default prod year when mill changes
  useEffect(() => {
    if (!form.mill_id || availableProdYears.length === 0) return;
    const assigned = millProdYearsMap[form.mill_id] || [];
    const defaultLink = assigned.find(l => l.is_default);
    const defaultYear = defaultLink
      ? availableProdYears.find(py => py.prod_year_id === defaultLink.prod_year_id)
      : availableProdYears[0];
    if (defaultYear) {
      setForm(f => ({
        ...f,
        prod_year_id: defaultYear.prod_year_id,
        prod_year: defaultYear.prod_year,
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.mill_id]);

  // State → District → Pincode chain removed (delivery destination section removed)

  // ─── Load existing order ────────────────────────────────────────────────────
  const loadOrder = useCallback((o) => {
    const todayStr = today();
    setExistingOrder(o);

    // Determine broker read-only
    const isFinalized = ['SENT_TO_MILL', 'FINAL', 'MODIFIED'].includes(o.status);
    setBrokerReadOnly(isBroker && isFinalized);

    setForm({
      sale_order_flag: o.sale_order_flag ?? o.sell_order_flag ?? true,
      is_credit: o.is_credit ?? false,
      mill_id: o.factory_id || '',
      prod_year_id: o.prod_year_id || '',
      prod_year: o.prod_year || '',
      truck_id: o.truck_id || '',
      truck_no: o.truck_no || '',
      driver_name: o.driver_name || '',
      assigned_to_broker_id: o.assigned_to_broker_id || '',
      include_lab_reports: o.include_lab_reports ?? false,
    });

    // Group items by bill_to_name
    const groupMap = {};
    const groupOrder = [];
    for (const item of (o.items || [])) {
      const key = item.bill_to_name || '__default__';
      if (!groupMap[key]) {
        groupMap[key] = {
          pg_id: newPgId(),
          bill_to_name: item.bill_to_name || '',
          bill_to_address: item.bill_to_address || '',
          bill_to_gst: item.bill_to_gst || '',
          bill_to_is_194q: item.bill_to_is_194q || false,
          ship_to_name: item.ship_to_name || '',
          ship_to_address: item.ship_to_address || '',
          ship_to_gst: item.ship_to_gst || '',
          subitems: [],
          same_bgn: false,
        };
        groupOrder.push(key);
      }
      groupMap[key].subitems.push({
        item_id: item.item_id || newItemId(),
        grade_id: item.grade_id || '',
        grade_name: item.grade_name || '',
        quantity: item.quantity?.toString() || '',
        price: item.price?.toString() || '',
        bgn_number: item.bgn_number || '',
        production_year: item.production_year || '',
        prod_year_id: item.prod_year_id || '',
        penalty: item.penalty?.toString() || '',
      });
    }

    // Map payment details back
    const paymentByIdx = {};
    (o.party_payments || []).forEach(p => { paymentByIdx[p.party_index] = p; });

    const groups = groupOrder.map((k, idx) => {
      const payment = paymentByIdx[idx];
      return {
        ...groupMap[k],
        show_payment: !!payment,
        payment_date: payment?.date || todayStr,
        payment_amount: payment?.amount?.toString() || '',
        payment_details: payment?.details || '',
        payment_image_base64: payment?.image_base64 || '',
        expanded: idx === 0,
      };
    });

    setPartyGroups(groups.length > 0 ? groups : [emptyPartyGroup()]);
  }, [isBroker]);

  useEffect(() => {
    if (!orderId) { setPageLoading(false); return; }
    api.get(`/orders/${orderId}`)
      .then(r => { loadOrder(r.data); setPageLoading(false); })
      .catch(e => {
        setError(e.response?.data?.detail || 'Failed to load order');
        setPageLoading(false);
      });
  }, [orderId, loadOrder]);

  // Check pending requests for this order
  useEffect(() => {
    if (!orderId || !isBroker) return;
    api.get(`/orders/${orderId}/truck-edit-requests`)
      .then(r => setHasPendingTruckEdit((r.data || []).some(req => req.status === 'PENDING')))
      .catch(() => {});
    api.get(`/orders/${orderId}/sale-order-edit-requests`)
      .then(r => setHasPendingSaleOrderEdit((r.data || []).some(req => req.status === 'PENDING')))
      .catch(() => {});
  }, [orderId, isBroker]);

  // ─── Party group helpers ────────────────────────────────────────────────────
  const updatePg = (pgIdx, updates) =>
    setPartyGroups(prev => prev.map((pg, i) => i === pgIdx ? { ...pg, ...updates } : pg));

  const updateSi = (pgIdx, siIdx, updates) =>
    setPartyGroups(prev => prev.map((pg, i) => i === pgIdx ? {
      ...pg,
      subitems: pg.subitems.map((si, j) => j === siIdx ? { ...si, ...updates } : si),
    } : pg));

  const addPartyGroup = () => {
    setPartyGroups(prev => [
      ...prev.map(pg => ({ ...pg, expanded: false })),
      { ...emptyPartyGroup(), expanded: true },
    ]);
  };

  const removePartyGroup = (pgIdx) =>
    setPartyGroups(prev => prev.filter((_, i) => i !== pgIdx));

  const toggleExpand = (pgIdx) =>
    setPartyGroups(prev => prev.map((pg, i) => ({
      ...pg,
      expanded: i === pgIdx ? !pg.expanded : false,
    })));

  // ─── Bill To quick add ──────────────────────────────────────────────────────
  const handleBillToQuickAdd = async (pgIdx, name) => {
    try {
      const res = await api.post('/masters/parties', { name });
      const p = res.data;
      setParties(prev => [...prev, p]);
      updatePg(pgIdx, { bill_to_name: p.name, bill_to_address: p.address || '', bill_to_gst: p.gst || '' });
      showToast('success', `Party "${name}" added`);
    } catch { showToast('error', 'Failed to create party'); }
  };

  // ─── Truck helpers ──────────────────────────────────────────────────────────
  const handleTruckSelect = (id, truck) => {
    setForm(f => ({
      ...f,
      truck_id: id,
      truck_no: normalizeTruck(truck?.reg_no || ''),
      driver_name: truck?.driver_name || f.driver_name,
    }));
  };

  const handleTruckQuickAdd = async (regNo) => {
    const normalized = normalizeTruck(regNo);
    try {
      const res = await api.post('/masters/trucks', { reg_no: normalized });
      const t = res.data;
      setTrucks(prev => [...prev, t]);
      setForm(f => ({ ...f, truck_id: t.truck_id, truck_no: normalized }));
      showToast('success', `Truck ${normalized} added`);
    } catch { showToast('error', 'Failed to add truck'); }
  };

  // ─── Build payload ──────────────────────────────────────────────────────────
  const buildPayload = () => {
    const selectedMill = mills.find(m => m.mill_id === form.mill_id);
    const millGstRate = selectedMill?.gst_rate || 0;
    const millInsuranceRate = selectedMill?.insurance_rate || 0;

    const processedItems = partyGroups.flatMap(pg =>
      pg.subitems
        .filter(si => si.grade_id || parseFloat(si.quantity) > 0)
        .map(si => ({
          item_id: si.item_id,
          mill_id: form.mill_id,
          mill_name: selectedMill?.name || '',
          grade_id: si.grade_id,
          grade_name: si.grade_name,
          quantity: parseFloat(si.quantity) || 0,
          price: !isBroker && si.price ? parseFloat(si.price) : undefined,
          penalty: !isBroker && si.penalty ? parseFloat(si.penalty) : 0,
          mill_gst_rate: millGstRate,
          mill_insurance_rate: millInsuranceRate,
          bgn_number: !isBroker && si.bgn_number ? si.bgn_number : undefined,
          production_year: !isBroker && si.production_year ? si.production_year : undefined,
          bill_to_name: pg.bill_to_name || '',
          bill_to_address: pg.bill_to_address || '',
          bill_to_gst: pg.bill_to_gst || '',
          bill_to_is_194q: pg.bill_to_is_194q || false,
          ship_to_name: pg.ship_to_name || '',
          ship_to_address: pg.ship_to_address || '',
          ship_to_gst: pg.ship_to_gst || '',
        }))
    );

    const party_payments = partyGroups
      .filter(pg => pg.show_payment && (pg.payment_amount || pg.payment_details || pg.payment_image_base64))
      .map((pg, idx) => ({
        party_index: idx,
        bill_to_name: pg.bill_to_name || '',
        date: pg.payment_date || today(),
        amount: parseFloat(pg.payment_amount) || null,
        details: pg.payment_details || '',
        image_base64: pg.payment_image_base64 || null,
      }));

    return {
      sale_order_flag: form.sale_order_flag,
      is_credit: false,  // Credit is now set via Dashboard "Send Credit Email" button
      factory_id: form.mill_id || null,
      prod_year_id: form.prod_year_id || null,
      prod_year: form.prod_year || null,
      truck_id: form.truck_id || null,
      truck_no: normalizeTruck(form.truck_no),
      driver_name: form.driver_name,
      assigned_to_broker_id: form.assigned_to_broker_id || null,
      include_lab_reports: form.include_lab_reports,
      items: processedItems.map(item => ({
        ...item,
        prod_year_id: form.prod_year_id || null,
        production_year: form.prod_year || null,
      })),
      party_payments,
    };
  };

  // ─── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError('');
    if (partyGroups.every(pg => !pg.bill_to_name)) {
      setError('At least one party (Bill To) is required');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (orderId) {
        await api.put(`/orders/${orderId}`, payload);
        showToast('success', 'Order updated successfully');
      } else {
        const res = await api.post('/orders/', payload);
        showToast('success', 'Order created');
        navigate('/dashboard');
      }
      loadMasters();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error saving order');
    } finally { setSaving(false); }
  };

  // ─── Broker Truck Edit Request ───────────────────────────────────────────────
  const handleSubmitTruckEdit = async () => {
    if (!truckEditModal?.new_truck_no) { setError('New truck number is required'); return; }
    setSaving(true);
    try {
      await api.post(`/orders/${orderId}/request-truck-edit`, {
        new_truck_no: normalizeTruck(truckEditModal.new_truck_no),
        new_truck_id: truckEditModal.new_truck_id || null,
        reason: truckEditModal.reason || '',
      });
      setTruckEditModal(null);
      setHasPendingTruckEdit(true);
      showToast('success', 'Truck edit request submitted for approval');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to submit request');
    } finally { setSaving(false); }
  };

  // ─── Broker Sale Order Edit Request ─────────────────────────────────────────
  const handleSubmitSaleOrderEdit = async () => {
    setSaving(true);
    try {
      await api.post(`/orders/${orderId}/request-sale-order-edit`, {
        reason: saleOrderEditModal?.reason || '',
      });
      setSaleOrderEditModal(null);
      setHasPendingSaleOrderEdit(true);
      showToast('success', 'Sale Order edit request submitted for approval');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to submit request');
    } finally { setSaving(false); }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  if (pageLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isEditMode = !!orderId;
  const isFinalized = existingOrder && ['SENT_TO_MILL', 'FINAL', 'MODIFIED'].includes(existingOrder.status);
  const isCancelled = existingOrder?.status === 'CANCELLED';
  const canSave = !isCancelled && !brokerReadOnly;

  return (
    <div className="max-w-4xl mx-auto space-y-5 pb-6" data-testid="billing-form">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium
          ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}
          data-testid="form-toast"
        >
          <CheckCircle size={16} /> {toast.msg}
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 font-['Manrope']">
            {isEditMode ? 'Edit Order' : 'New Delivery Order'}
          </h1>
          {existingOrder && (
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {existingOrder.do_number && (
                <span className="text-xs font-mono text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                  {existingOrder.do_number}
                </span>
              )}
              <span className={`text-xs font-bold px-2 py-0.5 rounded border
                ${existingOrder.status === 'CANCELLED' ? 'bg-red-100 text-red-700 border-red-200'
                  : ['SENT_TO_MILL', 'FINAL'].includes(existingOrder.status) ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                  : 'bg-slate-100 text-slate-600 border-slate-200'}`}
              >
                {existingOrder.status === 'SENT_TO_MILL' ? 'SENT TO MILL' : existingOrder.status}
              </span>
            </div>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-slate-500 shrink-0">
          ← Back
        </Button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-center justify-between"
          data-testid="form-error">
          {error}
          <button onClick={() => setError('')}><X size={14} /></button>
        </div>
      )}

      {/* ─── Order Configuration ──────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
        <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Order Configuration</h2>

        <div className="grid sm:grid-cols-2 gap-4">
          {/* Mill */}
          <div>
            <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">
              Mill *
            </Label>
            <Combobox
              items={mills}
              value={form.mill_id}
              onChange={(id) => setForm(f => ({ ...f, mill_id: id }))}
              valueKey="mill_id"
              searchKey="name"
              placeholder="Select mill..."
              disabled={brokerReadOnly}
              data-testid="mill-combobox"
            />
          </div>

          {/* Sale Order Toggle */}
          <div>
            <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">
              Sale Order{' '}
              <span className="text-[10px] font-normal normal-case text-slate-400">
                (No = Truck required)
              </span>
            </Label>
            {brokerReadOnly ? (
              <div className="flex items-center gap-2 h-9">
                <span className={`text-sm font-bold ${form.sale_order_flag ? 'text-blue-700' : 'text-orange-700'}`}>
                  {form.sale_order_flag ? 'YES' : 'NO'}
                </span>
                {!hasPendingSaleOrderEdit ? (
                  <button
                    onClick={() => setSaleOrderEditModal({ reason: '' })}
                    className="text-xs text-slate-500 border border-slate-300 px-2 py-1 rounded hover:bg-slate-50"
                    data-testid="request-sale-order-edit-btn"
                  >
                    Request Edit
                  </button>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-amber-600 border border-amber-200 bg-amber-50 px-2 py-1 rounded">
                    <Clock size={11} /> Pending Approval
                  </span>
                )}
              </div>
            ) : (
              <button
                onClick={() => setForm(f => ({ ...f, sale_order_flag: !f.sale_order_flag }))}
                className={`h-9 px-4 rounded-md border text-sm font-semibold transition-colors
                  ${form.sale_order_flag
                    ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                    : 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100'
                  }`}
                data-testid="sale-order-toggle"
              >
                {form.sale_order_flag ? 'YES — Sale Order' : 'NO — Truck Required'}
              </button>
            )}
          </div>

          {/* Production Year — shown below Mill when mill is selected */}
          {form.mill_id && (
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">
                Production Year
              </Label>
              {availableProdYears.length === 0 ? (
                <div className="h-9 flex items-center text-sm text-slate-400">No production years configured</div>
              ) : availableProdYears.length === 1 ? (
                <div className="h-9 flex items-center gap-2">
                  <span className="px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-md text-sm font-semibold">
                    {availableProdYears[0].prod_year}
                  </span>
                </div>
              ) : (
                <select
                  value={form.prod_year_id}
                  onChange={e => {
                    const selected = availableProdYears.find(y => y.prod_year_id === e.target.value);
                    setForm(f => ({ ...f, prod_year_id: e.target.value, prod_year: selected?.prod_year || '' }));
                  }}
                  className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white w-full focus:outline-none focus:ring-2 focus:ring-blue-500 h-9"
                  disabled={brokerReadOnly}
                  data-testid="prod-year-select"
                >
                  <option value="">— Select Year —</option>
                  {availableProdYears.map(y => (
                    <option key={y.prod_year_id} value={y.prod_year_id}>{y.prod_year}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Credit Order (SuperAdmin only) — REMOVED from form. Use Dashboard "Send Credit Email" button */}

          {/* Assign to Broker (Admin only) */}
          {isAdminOrSuperAdmin && (
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">
                Assign to Broker
              </Label>
              <select
                value={form.assigned_to_broker_id}
                onChange={e => setForm(f => ({ ...f, assigned_to_broker_id: e.target.value }))}
                className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="assign-broker-select"
              >
                <option value="">— Unassigned —</option>
                {brokers.map(b => <option key={b.user_id} value={b.user_id}>{b.name}</option>)}
              </select>
            </div>
          )}

          {/* Include Lab Reports */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="include_lab_reports"
              checked={form.include_lab_reports}
              onChange={e => setForm(f => ({ ...f, include_lab_reports: e.target.checked }))}
              className="w-4 h-4 rounded border-slate-300 text-blue-600"
              disabled={brokerReadOnly}
              data-testid="include-lab-reports"
            />
            <label htmlFor="include_lab_reports" className="text-sm text-slate-700 font-medium flex items-center gap-1.5 cursor-pointer">
              <FlaskConical size={14} className="text-slate-400" />
              Include Lab Reports
            </label>
          </div>
        </div>
      </div>

      {/* ─── Transport Details ────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
        <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">
          Transport Details{!form.sale_order_flag && <span className="ml-2 text-orange-600 text-xs font-normal normal-case">(Truck required)</span>}
        </h2>

        <div className="grid sm:grid-cols-2 gap-4">
          {/* Truck */}
          <div>
            <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">
              Truck No.{!form.sale_order_flag && ' *'}
            </Label>
            {brokerReadOnly ? (
              <div className="flex items-center gap-2">
                <div className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-700 bg-slate-50 font-mono">
                  {form.truck_no || <span className="text-slate-400 italic">Not assigned</span>}
                </div>
                {!hasPendingTruckEdit ? (
                  <button
                    onClick={() => setTruckEditModal({ new_truck_no: form.truck_no || '', new_truck_id: '', reason: '' })}
                    className="text-xs text-slate-500 border border-slate-300 px-2 py-1.5 rounded hover:bg-slate-50 whitespace-nowrap"
                    data-testid="request-truck-edit-btn"
                  >
                    Request Edit
                  </button>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-amber-600 border border-amber-200 bg-amber-50 px-2 py-1 rounded">
                    <Clock size={11} /> Pending
                  </span>
                )}
              </div>
            ) : (
              <Combobox
                items={trucks}
                value={form.truck_id}
                onChange={handleTruckSelect}
                valueKey="truck_id"
                searchKey="reg_no"
                placeholder="Search or add truck..."
                canCreate={true}
                onQuickAdd={handleTruckQuickAdd}
                data-testid="truck-combobox"
              />
            )}
          </div>

          {/* Driver */}
          <div>
            <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">Driver Name</Label>
            <Input
              value={form.driver_name}
              onChange={e => setForm(f => ({ ...f, driver_name: e.target.value }))}
              placeholder="Driver name (optional)"
              className="text-sm"
              disabled={brokerReadOnly}
              data-testid="driver-input"
            />
          </div>
        </div>
      </div>

      {/* ─── Party Groups ─────────────────────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Parties & Items</h2>
          {!brokerReadOnly && (
            <button
              onClick={addPartyGroup}
              className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium border border-blue-200 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-md transition-colors"
              data-testid="add-party-btn"
            >
              <Plus size={13} /> Add Party
            </button>
          )}
        </div>

        {partyGroups.map((pg, pgIdx) => (
          <PartyGroupCard
            key={pg.pg_id}
            pg={pg}
            pgIdx={pgIdx}
            totalGroups={partyGroups.length}
            isBroker={isBroker}
            availableGrades={gradesForMill}
            parties={parties}
            brokerReadOnly={brokerReadOnly}
            onUpdatePg={updates => updatePg(pgIdx, updates)}
            onUpdateSi={(siIdx, updates) => updateSi(pgIdx, siIdx, updates)}
            onGrade={(siIdx, id, g) => updateSi(pgIdx, siIdx, { grade_id: id, grade_name: g?.name || '' })}
            onAddSi={() => updatePg(pgIdx, { subitems: [...pg.subitems, emptySubItem()] })}
            onRemoveSi={siIdx => updatePg(pgIdx, { subitems: pg.subitems.filter((_, j) => j !== siIdx) })}
            onRemovePg={() => removePartyGroup(pgIdx)}
            onToggleExpand={() => toggleExpand(pgIdx)}
            onBillToSelect={(id, p) => updatePg(pgIdx, {
              bill_to_name: p.name,
              bill_to_address: p.address || '',
              bill_to_gst: p.gst || '',
              bill_to_is_194q: p.is_194q || false,
            })}
            onBillToQuickAdd={name => handleBillToQuickAdd(pgIdx, name)}
          />
        ))}
      </div>

      {/* ─── Action Buttons ───────────────────────────────────────────────── */}
      {!isCancelled && (
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} disabled={saving}>
            Cancel
          </Button>
          <div className="flex items-center gap-3">
            {canSave && (
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="save-btn"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2"><Save size={14} /> Save Order</span>
                )}
              </Button>
            )}
          </div>
        </div>
      )}
      {/* Mobile sticky save bar */}
      {!isCancelled && canSave && (
        <div className="sm:hidden fixed bottom-16 left-0 right-0 z-10 px-4 pb-3 pt-2 bg-white/95 backdrop-blur border-t border-slate-200">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-base font-semibold"
            data-testid="save-btn-mobile"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </span>
            ) : (
              <span className="flex items-center gap-2"><Save size={16} /> Save Order</span>
            )}
          </Button>
        </div>
      )}

      {isCancelled && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 text-center">
          This order has been cancelled and cannot be edited.
        </div>
      )}

      {/* ─── Truck Edit Request Modal ─────────────────────────────────────── */}
      {truckEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={e => e.target === e.currentTarget && setTruckEditModal(null)}
          data-testid="truck-edit-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <TruckIcon size={15} className="text-slate-500" />
                <h3 className="text-sm font-semibold text-slate-800">Request Truck Edit</h3>
              </div>
              <button onClick={() => setTruckEditModal(null)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <p className="text-xs text-slate-500">
                Current: <span className="font-mono font-semibold text-slate-700">{existingOrder?.truck_no || 'None'}</span>
              </p>
              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">New Truck No. *</Label>
                <Combobox
                  items={trucks}
                  value={truckEditModal.new_truck_id}
                  onChange={(id, t) => setTruckEditModal(m => ({
                    ...m,
                    new_truck_id: id,
                    new_truck_no: normalizeTruck(t?.reg_no || ''),
                  }))}
                  valueKey="truck_id"
                  searchKey="reg_no"
                  placeholder="Search or enter truck..."
                  canCreate={false}
                  data-testid="truck-edit-new-truck"
                />
                <Input
                  value={truckEditModal.new_truck_no}
                  onChange={e => setTruckEditModal(m => ({ ...m, new_truck_no: normalizeTruck(e.target.value), new_truck_id: '' }))}
                  placeholder="Or type manually..."
                  className="text-sm font-mono mt-2"
                  data-testid="truck-edit-manual-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">Reason (optional)</Label>
                <textarea
                  value={truckEditModal.reason}
                  onChange={e => setTruckEditModal(m => ({ ...m, reason: e.target.value }))}
                  placeholder="Why are you changing the truck?"
                  rows={2}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  data-testid="truck-edit-reason"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-slate-100">
              <Button variant="outline" size="sm" onClick={() => setTruckEditModal(null)}>Cancel</Button>
              <Button size="sm" onClick={handleSubmitTruckEdit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="truck-edit-submit-btn">
                {saving ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Sale Order Edit Request Modal ───────────────────────────────── */}
      {saleOrderEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={e => e.target === e.currentTarget && setSaleOrderEditModal(null)}
          data-testid="sale-order-edit-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-800">Request Sale Order Edit</h3>
              <button onClick={() => setSaleOrderEditModal(null)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <p className="text-xs text-slate-500">
                Change Sale Order from{' '}
                <span className={`font-bold ${form.sale_order_flag ? 'text-blue-700' : 'text-orange-700'}`}>
                  {form.sale_order_flag ? 'YES' : 'NO'}
                </span>
                {' → '}
                <span className={`font-bold ${!form.sale_order_flag ? 'text-blue-700' : 'text-orange-700'}`}>
                  {!form.sale_order_flag ? 'YES' : 'NO'}
                </span>
              </p>
              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">Reason (optional)</Label>
                <textarea
                  value={saleOrderEditModal.reason}
                  onChange={e => setSaleOrderEditModal(m => ({ ...m, reason: e.target.value }))}
                  placeholder="Reason for change..."
                  rows={3}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  data-testid="sale-order-edit-reason"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-slate-100">
              <Button variant="outline" size="sm" onClick={() => setSaleOrderEditModal(null)}>Cancel</Button>
              <Button size="sm" onClick={handleSubmitSaleOrderEdit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="sale-order-edit-submit-btn">
                {saving ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
